import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { sequenceSchema } from '../../interfaces/ISequence';
import { requiredStringSchema, coercedIdSchema } from '../schemas';

const coercedNumberSchema = z.preprocess((val) => {
	if (typeof val === 'number') return val;
	if (typeof val === 'string') {
		const parsed = Number(val);
		if (!Number.isNaN(parsed)) return parsed;
	}
	return val;
}, z.number());

const optionalCoercedNumberSchema = z.preprocess((val) => {
	if (val === undefined) return undefined;
	if (typeof val === 'number') return val;
	if (typeof val === 'string') {
		const parsed = Number(val);
		if (!Number.isNaN(parsed)) return parsed;
	}
	return val;
}, z.number().optional());

const optionalCoercedBooleanSchema = z.preprocess((val) => {
	if (val === undefined) return undefined;
	if (typeof val === 'boolean') return val;
	if (val === 'true') return true;
	if (val === 'false') return false;
	return val;
}, z.boolean().optional());

// ============================================================
// Param Schemas
// ============================================================

const projectParamsSchema = z.object({
	projectId: requiredStringSchema,
});

const sequenceParamsSchema = projectParamsSchema.extend({
	id: requiredStringSchema,
});

// ============================================================
// Contract
// ============================================================

export const sequenceContract = defineContract(
	'sequences',
	{
		getSequences: route({
			method: 'GET',
			path: '/projects/:projectId/sequences',
			params: projectParamsSchema,
			response: z.array(sequenceSchema),
			operationId: 'getSequencesByProjectId',
			tags: ['Sequences'],
			summary: 'Get all sequences for a project',
			description: 'Retrieves all sequences for the specified project, ordered by orderIndex ascending',
		}),
		createSequence: route({
			method: 'POST',
			path: '/projects/:projectId/sequences',
			params: projectParamsSchema,
			// No `projectId`: the path carries it, and it is the value the guard
			// verified. A body copy would be a second source of truth for the handler
			// to pick from.
			body: z.object({
				orderIndex: coercedNumberSchema,
				sequenceType: requiredStringSchema,
				name: requiredStringSchema,
			}),
			response: sequenceSchema,
			operationId: 'createSequence',
			tags: ['Sequences'],
			summary: 'Create a new sequence',
		}),
		updateSequence: route({
			method: 'PUT',
			path: '/projects/:projectId/sequences/:id',
			params: sequenceParamsSchema,
			// `projectId` omitted deliberately: accepting it here would let an update
			// move a sequence between projects.
			body: z.object({
				_id: coercedIdSchema.optional(),
				sequenceType: z.string().optional(),
				orderIndex: optionalCoercedNumberSchema,
				name: z.string().optional(),
				isInitial: optionalCoercedBooleanSchema,
			}),
			response: sequenceSchema.nullable(),
			operationId: 'updateSequenceById',
			tags: ['Sequences'],
			summary: 'Update a sequence',
		}),
		deleteSequence: route({
			method: 'DELETE',
			path: '/projects/:projectId/sequences/:id',
			params: sequenceParamsSchema,
			response: z.string(),
			operationId: 'deleteSequenceById',
			tags: ['Sequences'],
			summary: 'Delete a sequence',
			description: 'Deletes a sequence by ID along with all associated sequence items',
		}),
	},
	{ root: true }
);
